import React, {useState, useEffect} from 'react';
import KeyboardDoubleArrowDownOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowDownOutlined';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';
import {
    Button,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    fetchGlobalReportCompanies,
    fetchGlobalReportManagers,
    fetchGlobalReportPlatforms,
    fetchGlobalReportFields,
} from '../../../../API/GlobalReport';
import DateRangeSelector from '../../../../components/date/DateRangeSelector';
import SelectMulti from '../../../../components/select/SelectMulti';
import useApi from '../../../../hooks/useApi';
import Cookies from 'js-cookie';

function GlobalReportOptionsForm({onSubmit}) {
    const {data: companies} = useApi(fetchGlobalReportCompanies);
    const {data: managers} = useApi(fetchGlobalReportManagers);
    const {data: platforms} = useApi(fetchGlobalReportPlatforms);
    const {data: fields} = useApi(fetchGlobalReportFields);

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(12, 0, 0, 0);
    const weekStart = new Date(yesterday);
    weekStart.setDate(yesterday.getDate() - 6);

    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [showPartner, setShowPartner] = useState(false);
    const [showManager, setShowManager] = useState(false);
    const [showPlatform, setShowPlatform] = useState(false);
    const [showType, setShowType] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState([]);
    const [selectedManager, setSelectedManager] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState([]);
    const [selectedType, setSelectedType] = useState([]);

    const types = [
        {name: 'DSP', id: 'DSP'},
        {name: 'SSP', id: 'SSP'},
    ];

    const groupByOptions = [
        {name: 'Total', id: 'total'},
        {name: 'Day', id: 'day'},
    ];

    const [options, setOptions] = useState({
        start_date: formatLocalDate(weekStart),
        end_date: formatLocalDate(yesterday),
        fields: [],
        group_by: 'total',
        companies: [],
        managers: [],
        platform: [],
        type: [],
    });

    useEffect(() => {
        const storedValue = Cookies.get('GlobalReport');
        if (storedValue) {
            try {
                const parsed = JSON.parse(storedValue);
                delete parsed.start_date;
                delete parsed.end_date;
                delete parsed.group_by;
                setOptions((prev) => ({...prev, ...parsed}));
                if (parsed.companies) setSelectedPartner(parsed.companies);
                if (parsed.managers) setSelectedManager(parsed.managers);
                if (parsed.platform) setSelectedPlatform(parsed.platform);
                if (parsed.type) setSelectedType(parsed.type);
            } catch (e) {
                // ignore broken cookie
            }
        }
    }, []);

    const handleDateChange = (selection) => {
        const endDate = new Date(selection.endDate);
        endDate.setHours(12, 12, 12, 12);
        const startDate = new Date(selection.startDate);
        startDate.setHours(12, 12, 12, 12);

        setOptions((prevOptions) => ({
            ...prevOptions,
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0],
        }));
    };

    const handleCheckboxChangeField = (event) => {
        const {name, checked} = event.target;
        let nextFields = [...(options.fields || [])];
        if (checked) {
            nextFields.push(name);
        } else {
            nextFields = nextFields.filter((item) => item !== name);
        }
        setOptions({...options, fields: nextFields});
    };

    const handleGroupByChange = (event) => {
        setOptions((prev) => ({...prev, group_by: event.target.value}));
    };

    useEffect(() => {
        setOptions((prev) => ({...prev, companies: selectedPartner}));
    }, [selectedPartner]);

    useEffect(() => {
        setOptions((prev) => ({...prev, managers: selectedManager}));
    }, [selectedManager]);

    useEffect(() => {
        setOptions((prev) => ({...prev, platform: selectedPlatform}));
    }, [selectedPlatform]);

    useEffect(() => {
        setOptions((prev) => ({...prev, type: selectedType}));
    }, [selectedType]);

    const handleSubmit = () => {
        onSubmit(options);
    };

    return (
        <Grid container spacing={2} sx={{width: '100%', marginLeft: '0px'}}>
            <Accordion sx={{width: '100%'}}>
                <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownOutlinedIcon/>}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{backgroundColor: '#f0f3f5', width: '100%'}}
                >
                    Options
                </AccordionSummary>
                <AccordionDetails sx={{width: '100%'}}>
                    <Grid item xs={12} md={12} sx={{width: '100%', margin: '0px 0 25px auto'}}>
                        <Grid item xs={12} md={12}>
                            {Object.keys(fields).length !== 0 ? (<strong>Data</strong>) : ''}
                        </Grid>
                        {fields.map((field, index) => (
                            <FormControlLabel
                                key={index}
                                control={
                                    <Checkbox
                                        checked={(options.fields || []).includes(field.key)}
                                        onChange={handleCheckboxChangeField}
                                        name={field.key}
                                    />
                                }
                                label={field.value}
                            />
                        ))}
                    </Grid>

                    <Grid item xs={12} md={12} sx={{width: '100%', margin: '25px 0 25px auto'}}>
                        <Grid item xs={12} md={12}>
                            <strong>Filters</strong>
                        </Grid>
                        <FormControlLabel
                            control={<Checkbox checked={showPartner} onChange={() => setShowPartner(!showPartner)}/>}
                            label="Partner Name"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={showManager} onChange={() => setShowManager(!showManager)}/>}
                            label="Account Manager"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={showPlatform} onChange={() => setShowPlatform(!showPlatform)}/>}
                            label="Platform"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={showType} onChange={() => setShowType(!showType)}/>}
                            label="Type"
                        />
                        <Grid container spacing={2} sx={{marginTop: '8px'}}>
                            {showPartner && (
                                <Grid item xs={6}>
                                    <SelectMulti
                                        label="Partner Name"
                                        items={companies}
                                        selectedItems={selectedPartner}
                                        onChange={(event) => setSelectedPartner(event.target.value)}
                                        itemLabel="value.name"
                                        itemKey="value.id"
                                        itemValue="value.id"
                                    />
                                </Grid>
                            )}
                            {showManager && (
                                <Grid item xs={6}>
                                    <SelectMulti
                                        label="Account Manager"
                                        items={managers}
                                        selectedItems={selectedManager}
                                        onChange={(event) => setSelectedManager(event.target.value)}
                                        itemLabel="value.name"
                                        itemKey="value.id"
                                        itemValue="value.id"
                                    />
                                </Grid>
                            )}
                            {showPlatform && (
                                <Grid item xs={6}>
                                    <SelectMulti
                                        label="Platform"
                                        items={platforms}
                                        selectedItems={selectedPlatform}
                                        onChange={(event) => setSelectedPlatform(event.target.value)}
                                        itemLabel="value.name"
                                        itemKey="value.id"
                                        itemValue="value.id"
                                    />
                                </Grid>
                            )}
                            {showType && (
                                <Grid item xs={6}>
                                    <SelectMulti
                                        label="Type"
                                        items={types}
                                        selectedItems={selectedType}
                                        onChange={(event) => setSelectedType(event.target.value)}
                                        itemLabel="name"
                                        itemKey="id"
                                        itemValue="id"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={12} sx={{width: '100%', margin: '0px 0 25px auto'}}>
                    <Grid item xs={12} md={12}>
                            <strong>Group by</strong>
                        </Grid>
                        <FormControl sx={{minWidth: 240, marginTop: '20px'}}>
                            <Select
                                labelId="global-report-group-by-label"
                                id="global-report-group-by"
                                value={options.group_by}
                                onChange={handleGroupByChange}
                            >
                                {groupByOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Grid item xs={6} md={6}>
                <Button
                    size="large"
                    startIcon={<BackupTableOutlinedIcon fontSize="small"/>}
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    sx={{marginLeft: '-15px'}}
                >
                    Generate Report
                </Button>
            </Grid>
            <Grid item xs={6} md={6}>
                <DateRangeSelector
                    initialRange={{startDate: weekStart, endDate: yesterday, key: 'selection'}}
                    onRangeChange={handleDateChange}
                />
            </Grid>
        </Grid>
    );
}

export default GlobalReportOptionsForm;
